import styled from '@emotion/styled';
import { useMount } from 'ahooks';
import { message, Select, SelectProps, Tag } from 'antd';
import useCrud from 'hooks/useCrud';
import { useRef, useEffect, useCallback, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { DashboardType, getDepartmentList } from 'services/api/dashboard';
import { translateDepartment } from 'views/Assistant/utils';

export interface DepartmentSelectProps extends SelectProps {
  dashboardType: DashboardType;
  value?: Dashboard.Department;
  onChange: (value: Dashboard.Department) => void;
}

const useDepartmentSelect = (dashboardType: DashboardType) => {
  const { t } = useTranslate();

  const { loading, list, fetchList } = useCrud({
    readList: useCallback(
      (dashboardType: DashboardType) =>
        getDepartmentList(dashboardType).then((res) => res.departments),
      [],
    ),
  });

  const options = (list || []).map(({ id, name, isDefault, type }) => {
    return {
      value: id,
      label: translateDepartment({ type, name, isDefault }, t),
    };
  });

  useMount(() => {
    fetchList(dashboardType).catch(() => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    });
  });

  return {
    t,
    loading,
    list,
    options,
  };
};

function DepartmentSelect(props: DepartmentSelectProps) {
  const { dashboardType, value, onChange, ...otherProps } = props;

  const { t, loading, list, options } = useDepartmentSelect(dashboardType);

  const refsCurrent = { value, onChange };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const {
      current: { value, onChange },
    } = refs;

    if (value || !list || list.length === 0) {
      return;
    }

    const target = list.find(({ isDefault }) => isDefault === 1) || list[0];

    onChange(target);
  }, [list]);

  return (
    <Select
      style={{ width: '140px' }}
      loading={loading}
      options={options}
      placeholder={t('department.选择x', { x: t('department.部门') })}
      value={value ? value.id : undefined}
      onChange={(value) => {
        const target = list?.find(({ id }) => id === value);

        if (target) {
          onChange(target);
        }
      }}
      {...otherProps}
    />
  );
}

export interface DepartmentsSelectProps extends SelectProps {
  dashboardType: DashboardType;
  value?: Dashboard.Department[];
  onChange: (value: Dashboard.Department[]) => void;
}

export function DepartmentsSelect(props: DepartmentsSelectProps) {
  const {
    dashboardType,
    value: valueProp,
    onChange: onChangeProp,
    mode,
    onBlur,
    ...otherProps
  } = props;

  const allowClear = dashboardType === 'performance' || dashboardType === 'todo';

  const { t, loading, list, options } = useDepartmentSelect(dashboardType);

  const [value, setValue] = useState(undefined);
  useEffect(() => {
    if (!list || list?.length === 0) {
      return;
    }
    if (allowClear && valueProp?.length === list?.length) {
      return;
    }
    setValue(valueProp);
  }, [valueProp, allowClear, list]);

  const refsCurrent = { valueProp, onChangeProp };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const {
      current: { valueProp, onChangeProp },
    } = refs;

    if (valueProp || !list || list.length === 0) {
      return;
    }

    if (allowClear) {
      onChangeProp(list);
    } else {
      const targets = list.filter(({ isDefault }) => isDefault === 1);

      onChangeProp(targets.length > 0 ? targets : [list[0]]);
    }
  }, [list, allowClear]);

  return (
    <Select<number[]>
      className="w-48"
      mode="multiple"
      allowClear={allowClear}
      maxTagCount={3}
      loading={loading}
      options={options}
      placeholder={t('department.选择x', { x: t('department.部门') })}
      value={value?.map(({ id }) => id)}
      tagRender={({ label }) => <StyledTag>{label}</StyledTag>}
      onChange={(value) => {
        const next = value.reduce<Dashboard.Department[]>((res, cur) => {
          const target = list?.find(({ id }) => id === cur);

          if (target) {
            res.push(target);
          }

          return res;
        }, []);

        if (next.length > 0 || allowClear) {
          setValue(next);
        }
      }}
      onBlur={(e) => {
        if (value !== valueProp) {
          if (value?.length) {
            onChangeProp(value);
          } else if (allowClear && list) {
            onChangeProp(list);
          }
        }

        if (onBlur) {
          onBlur(e);
        }
      }}
      onClear={() => {
        if (allowClear && list) {
          setTimeout(() => {
            onChangeProp(list);
          });
        }
      }}
      {...otherProps}
    />
  );
}

const StyledTag = styled(Tag)`
  line-height: 24px;
  border: none;
  background: #f5f5f5;
`;

export default DepartmentSelect;
